import React, { useEffect, useMemo, useState } from 'react';
import { Check, ListChecks, Layers } from 'lucide-react';
import { getWeeklySummary } from '../services/profileService';
import { getCategoryColor } from '../constants/categories';
import './ActivityPage.css';

// Activity Log — shows the user's REAL recent activity from the backend
// (/api/weekly-summary/ → tasks completed in the last 7 days). No mock data.
function ActivityPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const tasks = await getWeeklySummary();
        if (active) {
          setActivities((tasks || []).map((t) => ({
            id: t.id,
            title: t.task_text,
            category: t.category || 'Other',
          })));
        }
      } catch (_) {
        if (active) setActivities([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Category chips are derived from the actual data, so we never show a filter
  // that has nothing behind it.
  const categories = useMemo(
    () => Array.from(new Set(activities.map((a) => a.category))),
    [activities],
  );

  const shown = filter === 'all' ? activities : activities.filter((a) => a.category === filter);

  return (
    <div className="activity-wrapper">
      <div className="activity-card">
        <div className="activity-header">
          <h1>Activity Log</h1>
          <p>Tasks you've completed in the last 7 days</p>
        </div>

        {categories.length > 0 && (
          <div className="activity-filters">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                className={`filter-btn ${filter === c ? 'active' : ''}`}
                onClick={() => setFilter(c)}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="activity-content">
          {loading ? (
            <div className="no-activity"><p>Loading your activity…</p></div>
          ) : shown.length > 0 ? (
            <div className="activity-timeline">
              {shown.map((activity) => {
                const color = getCategoryColor(activity.category);
                return (
                  <div key={activity.id} className="activity-item">
                    <div
                      className="activity-icon"
                      style={{ backgroundColor: `color-mix(in srgb, ${color} 20%, transparent)`, color }}
                    >
                      <Check size={20} strokeWidth={3} />
                    </div>
                    <div className="activity-details">
                      <p className="activity-title">Completed “{activity.title}”</p>
                      <p className="activity-time">{activity.category}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-activity">
              <p>No completed tasks in the last 7 days yet. Finish a task and it'll show up here.</p>
            </div>
          )}
        </div>

        <div className="activity-stats">
          <h2>This Week's Summary</h2>
          <div className="stats-grid">
            <div className="stat-box">
              <div className="stat-value">{activities.length}</div>
              <div className="stat-label"><ListChecks size={14} /> Tasks Completed</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">{categories.length}</div>
              <div className="stat-label"><Layers size={14} /> Categories</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActivityPage;
